<template>
  <div class="build-hub">
    <header class="hub-header">
      <div>
        <h1>Build</h1>
        <p class="hub-subtitle">
          House cost spine, budgets, pricing, field tools, and market screens —
          pick a workspace, then go work it.
        </p>
      </div>
    </header>

    <div class="build-bento">
      <!-- Primary: Construction dashboard -->
      <section class="bento-purpose bento-primary">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-hard-hat"></i>
        </div>
        <div class="purpose-body">
          <h2>Construction dashboard</h2>
          <p>
            House fleet, cost codes, and document matching against the
            <strong>Builder OS house cost spine</strong> — the day-to-day ops view.
          </p>
          <ul class="purpose-keypoints">
            <li>Houses, budgets, and matched documents in one place</li>
            <li>Master data audit and builder tools from the fleet</li>
          </ul>
          <div class="purpose-actions">
            <router-link to="/construction-dashboard" class="btn btn-primary btn-sm">
              Open construction
            </router-link>
            <router-link to="/master-data-audit" class="btn btn-ghost btn-sm">
              Master data audit
            </router-link>
          </div>
        </div>
      </section>

      <!-- Elevation budgets -->
      <section class="bento-purpose bento-budgets">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-chart-bar"></i>
        </div>
        <div class="purpose-body">
          <h2>Elevation budgets</h2>
          <p>
            Budget health across plans and elevations — compare development budgets
            and drill into loan draws per development.
          </p>
          <div class="purpose-actions">
            <router-link to="/development-budgets" class="btn btn-primary btn-sm">
              Open budgets
            </router-link>
          </div>
        </div>
      </section>

      <!-- House pricing -->
      <section class="bento-purpose bento-pricing">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-calculator"></i>
        </div>
        <div class="purpose-body">
          <h2>House pricing</h2>
          <p>
            Build pricing scenarios with options and elevations, then save or compare
            scenarios before committing a configuration.
          </p>
          <div class="purpose-actions">
            <router-link to="/house-pricing" class="btn btn-primary btn-sm">
              Open scenario builder
            </router-link>
            <router-link to="/house-pricing/scenarios" class="btn btn-ghost btn-sm">
              Saved scenarios
            </router-link>
          </div>
        </div>
      </section>

      <!-- WIP -->
      <section class="bento-purpose bento-wip">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-tasks"></i>
        </div>
        <div class="purpose-body">
          <h2>WIP board</h2>
          <p>
            Work-in-progress across houses — see where builds stand and what still
            needs attention on the cost spine.
          </p>
          <div class="purpose-actions">
            <router-link to="/construction-wip" class="btn btn-primary btn-sm">
              Open WIP board
            </router-link>
          </div>
        </div>
      </section>

      <!-- Cash & draws — higher visual weight -->
      <section class="bento-purpose bento-cash">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-university"></i>
        </div>
        <div class="purpose-body">
          <h2>Cash, draws &amp; payables</h2>
          <p>
            Loan draw history, bank schedule upload, gap analysis, and vendor invoice
            auto-coding for ERP import — money flows against the house spine.
          </p>
          <ul class="purpose-keypoints">
            <li>Draws &amp; cash flow with 3-type gap analysis</li>
            <li>Invoice upload → AI extract → cost code suggestions</li>
            <li>Gap resolution when budget vs actual drifts</li>
          </ul>
          <div class="purpose-actions">
            <router-link to="/loan-draws" class="btn btn-primary btn-sm">
              Loan draws
            </router-link>
            <router-link to="/invoice-upload" class="btn btn-ghost btn-sm">
              Invoice upload
            </router-link>
            <router-link to="/gap-resolution" class="btn btn-ghost btn-sm">
              Gap resolution
            </router-link>
          </div>
        </div>
      </section>

      <!-- Buyer selections -->
      <section class="bento-purpose bento-buyer">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-palette"></i>
        </div>
        <div class="purpose-body">
          <h2>Buyer selections</h2>
          <p>
            Review and update buyer option selections for a house — internal view
            ahead of the full buyer portal.
          </p>
          <div class="purpose-actions">
            <router-link to="/buyer-portal" class="btn btn-primary btn-sm">
              Open selections
            </router-link>
          </div>
        </div>
      </section>

      <!-- Equity / market screens (alongside Build — commodities & valuation refs) -->
      <section class="bento-purpose bento-equity">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-balance-scale"></i>
        </div>
        <div class="purpose-body">
          <h2>Equity screens</h2>
          <p>
            Trailing P/E &lt; 40 on S&amp;P 500 / MidCap 400 / SmallCap 600 —
            market valuation screens that sit with Build tools alongside
            commodity and cost references.
          </p>
          <div class="purpose-actions">
            <router-link to="/wealth/equity-screens" class="btn btn-primary btn-sm">
              Open equity screens
            </router-link>
          </div>
        </div>
      </section>

      <!-- More tools (secondary) -->
      <section class="bento-purpose bento-purpose--tools">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-th-large"></i>
        </div>
        <div class="purpose-body">
          <h2>More tools</h2>
          <ul class="tool-links">
            <li>
              <router-link to="/knowledge-graph">Knowledge graph</router-link>
              <span class="tool-hint">Plans, specs, takeoffs</span>
            </li>
            <li>
              <router-link to="/permit-completion">Permit completion</router-link>
              <span class="tool-hint">Auto-fill from ERP (Lexington County)</span>
            </li>
            <li>
              <router-link to="/duplicate-detection">Duplicate detection</router-link>
              <span class="tool-hint">POs and document dupes</span>
            </li>
            <li>
              <router-link to="/database-connections">Data warehouse</router-link>
              <span class="tool-hint">SOR / warehouse connections</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/**
 * Build spoke hub — orientation bento for construction / Builder OS tools.
 * Sparse nav lands here; deep work lives on spoke routes (not this page).
 * @see docs/patterns/spoke-bento-cells.md
 * @see docs/product/HUB_SPOKE_PORTAL.md
 */
export default {
  name: 'BuildHub',
};
</script>

<style scoped>
.build-hub {
  max-width: 1100px;
  margin: 0;
  padding-top: 0.5rem;
}

.hub-header {
  margin-bottom: 1.25rem;
}

.hub-header h1 {
  margin: 0;
  font-size: 1.75rem;
  color: var(--p-ink, #1e293b);
}

.hub-subtitle {
  margin: 0.35rem 0 0;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
  max-width: 40rem;
}

.build-bento {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1rem;
  grid-template-areas:
    "primary primary primary primary primary primary"
    "budgets budgets pricing pricing wip wip"
    "cash cash cash cash buyer buyer"
    "equity equity equity equity equity equity"
    "tools tools tools tools tools tools";
}

.bento-primary { grid-area: primary; }
.bento-budgets { grid-area: budgets; }
.bento-pricing { grid-area: pricing; }
.bento-wip { grid-area: wip; }
.bento-cash { grid-area: cash; }
.bento-buyer { grid-area: buyer; }
.bento-equity { grid-area: equity; }
.bento-purpose--tools { grid-area: tools; }

.bento-purpose {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  min-height: 8.25rem;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.purpose-body h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: var(--p-ink, #1e293b);
}

.purpose-body p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--p-ink-muted, #64748b);
}

.purpose-keypoints {
  margin: 0.65rem 0 0;
  padding: 0 0 0 1.1rem;
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.purpose-keypoints li + li {
  margin-top: 0.2rem;
}

.purpose-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.bento-purpose--tools .tool-links {
  list-style: none;
  margin: 0.15rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem 1.25rem;
}

.tool-links li {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.tool-links a {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-accent, #1e3a5f);
  text-decoration: none;
}

.tool-links a:hover {
  text-decoration: underline;
}

.tool-hint {
  font-size: 0.75rem;
  color: var(--p-ink-muted, #64748b);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  padding: 0.4rem 0.75rem;
}

.btn-sm {
  padding: 0.35rem 0.7rem;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: #fff;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  background: transparent;
  color: var(--p-accent, #1e3a5f);
  border: 1px solid var(--p-border, #e2e8f0);
}

.btn-ghost:hover {
  background: var(--p-surface-muted, #f1f5f9);
}

@media (max-width: 900px) {
  .build-bento {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "primary primary"
      "budgets pricing"
      "wip buyer"
      "cash cash"
      "equity equity"
      "tools tools";
  }

  .bento-purpose--tools .tool-links {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .build-bento {
    grid-template-columns: 1fr;
    grid-template-areas:
      "primary"
      "budgets"
      "pricing"
      "wip"
      "cash"
      "buyer"
      "equity"
      "tools";
  }

  .bento-purpose--tools .tool-links {
    grid-template-columns: 1fr;
  }
}
</style>
